"use client";

import {
  PaletteColor,
  SimplePaletteColorOptions,
  createTheme,
} from "@mui/material";
import { ComponentsOverrides } from "@mui/material/styles";
import {
  chartColorPalette,
  chartColorPaletteTypes,
  colorPalette,
  colorPaletteTypes,
} from "./colors";
import { huHU } from "./locale";

declare module "@mui/material/styles" {
  interface TypographyVariants {
    weeklyEvent: React.CSSProperties;
  }

  interface TypographyVariantsOptions {
    weeklyEvent: React.CSSProperties;
  }
}

declare module "@mui/material/styles" {
  interface Palette {
    beigeDark: PaletteColor;
    beigeLight: PaletteColor;
    blueBase: PaletteColor;
    blueGray: PaletteColor;
    blueLight: PaletteColor;
    brownDark: PaletteColor;
    darkBase: PaletteColor;
    gray10: PaletteColor;
    gray20: PaletteColor;
    gray30: PaletteColor;
    gray40: PaletteColor;
    gray50: PaletteColor;
    gray60: PaletteColor;
    gray80: PaletteColor;
    gray90: PaletteColor;
    greenBase: PaletteColor;
    greenLight: PaletteColor;
    pinkLight: PaletteColor;
    purpleDark: PaletteColor;
    purpleLight: PaletteColor;
    redLight: PaletteColor;
    redMid: PaletteColor;
    redSmooth: PaletteColor;
    tealBase: PaletteColor;
    turkizLight: PaletteColor;
    white: PaletteColor;
    yellowBase: PaletteColor;
    yellowDark: PaletteColor;
    owGray: PaletteColor;
    fuguOrange: PaletteColor;
  }

  interface PaletteOptions {
    beigeDark: SimplePaletteColorOptions;
    beigeLight: SimplePaletteColorOptions;
    blueBase: SimplePaletteColorOptions;
    blueGray: SimplePaletteColorOptions;
    blueLight: SimplePaletteColorOptions;
    brownDark: SimplePaletteColorOptions;
    darkBase: SimplePaletteColorOptions;
    gray10: SimplePaletteColorOptions;
    gray20: SimplePaletteColorOptions;
    gray30: SimplePaletteColorOptions;
    gray40: SimplePaletteColorOptions;
    gray50: SimplePaletteColorOptions;
    gray60: SimplePaletteColorOptions;
    gray80: SimplePaletteColorOptions;
    gray90: SimplePaletteColorOptions;
    greenBase: SimplePaletteColorOptions;
    greenLight: SimplePaletteColorOptions;
    pinkLight: SimplePaletteColorOptions;
    purpleDark: SimplePaletteColorOptions;
    purpleLight: SimplePaletteColorOptions;
    redLight: SimplePaletteColorOptions;
    redMid: SimplePaletteColorOptions;
    redSmooth: SimplePaletteColorOptions;
    tealBase: SimplePaletteColorOptions;
    turkizLight: SimplePaletteColorOptions;
    white: SimplePaletteColorOptions;
    yellowBase: SimplePaletteColorOptions;
    yellowDark: SimplePaletteColorOptions;
    owGray: SimplePaletteColorOptions;
    fuguOrange: SimplePaletteColorOptions;
  }
}

declare module "@mui/material/styles" {
  interface Theme {
    colors: colorPaletteTypes;
    chartColors: chartColorPaletteTypes;
  }
  // allow configuration using `createTheme`
  interface ThemeOptions {
    colors?: colorPaletteTypes;
    chartColors?: chartColorPaletteTypes;
  }
}

interface StatProps {
  variant?: "light" | "dark";
}

declare module "@mui/material/styles" {
  interface ComponentNameToClassKey {
    OwDrawerHeader: "root";
  }

  interface ComponentsPropsList {
    OwDrawerHeader: Partial<StatProps>;
  }

  interface Components {
    OwDrawerHeader?: {
      styleOverrides?: ComponentsOverrides<Theme>["OwDrawerHeader"];
    };
  }
}

const theme = createTheme();

export const ocLightTheme = createTheme(
  {
    colors: colorPalette,
    chartColors: chartColorPalette,
    typography: {
      fontFamily: [
        "Mulish",
        "Roboto",
        '"Helvetica Neue"',
        "Arial",
        "sans-serif",
      ].join(","),
      h1: {
        fontSize: "96px",
        fontStyle: "normal",
        fontWeight: 400,
        lineHeight: "normal",
        letterSpacing: "-1.5px",
      },
      h2: {
        fontSize: "60px",
        fontStyle: "normal",
        fontWeight: 400,
        lineHeight: "normal",
      },
      h3: {
        fontSize: "48px",
        fontStyle: "normal",
        fontWeight: 400,
        lineHeight: "normal",
      },
      h4: {
        fontSize: "34px",
        fontStyle: "normal",
        fontWeight: 400,
        lineHeight: "normal",
        letterSpacing: "0.25px",
      },
      h5: {
        fontSize: "24px",
        fontStyle: "normal",
        fontWeight: 400,
        lineHeight: "normal",
      },
      h6: {
        fontSize: "20px",
        fontStyle: "normal",
        fontWeight: 400,
        lineHeight: "28px",
        letterSpacing: "0.15px",
      },
      body1: {
        fontSize: "16px",
        fontStyle: "normal",
        fontWeight: 400,
        lineHeight: "24px",
        letterSpacing: "0.44px",
      },
      body2: {
        fontSize: "14px",
        fontStyle: "normal",
        fontWeight: 400,
        lineHeight: "20px",
        letterSpacing: "0.25px",
      },
      caption: {
        fontSize: "12px",
        fontStyle: "normal",
        fontWeight: 400,
        lineHeight: "16px",
        letterSpacing: "0.4px",
      },
      button: {
        fontSize: "14px",
        fontStyle: "normal",
        fontWeight: 500,
        lineHeight: "normal",
        letterSpacing: "0.75px",
        textTransform: "uppercase",
      },
      weeklyEvent: {
        fontSize: "10px",
        lineHeight: "normal",
      },
    },
    palette: {
      text: {
        primary: colorPalette.darkBase,
      },
      primary: theme.palette.augmentColor({
        color: {
          main: colorPalette.primary,
        },
        name: "primary",
      }),
      secondary: theme.palette.augmentColor({
        color: {
          main: "#5E6366",
        },
        name: "primary",
      }),
      success: theme.palette.augmentColor({
        color: {
          main: colorPalette.greenBase,
        },
        name: "success",
      }),
      error: theme.palette.augmentColor({
        color: {
          main: colorPalette.primary,
        },
        name: "error",
      }),
      warning: theme.palette.augmentColor({
        color: {
          main: colorPalette.fuguOrange,
        },
        name: "warning",
      }),
      info: theme.palette.augmentColor({
        color: {
          main: colorPalette.gray40,
        },
        name: "info",
      }),
      beigeDark: theme.palette.augmentColor({
        color: {
          main: colorPalette.beigeDark,
        },
        name: "beigeDark",
      }),
      beigeLight: theme.palette.augmentColor({
        color: {
          main: colorPalette.beigeLight,
        },
        name: "beigeLight",
      }),
      blueBase: theme.palette.augmentColor({
        color: {
          main: colorPalette.blueBase,
        },
        name: "blueBase",
      }),
      blueGray: theme.palette.augmentColor({
        color: {
          main: colorPalette.blueGray,
        },
        name: "blueGray",
      }),
      blueLight: theme.palette.augmentColor({
        color: {
          main: colorPalette.blueLight,
        },
        name: "blueLight",
      }),
      brownDark: theme.palette.augmentColor({
        color: {
          main: colorPalette.brownDark,
        },
        name: "brownDark",
      }),
      darkBase: theme.palette.augmentColor({
        color: {
          main: colorPalette.darkBase,
        },
        name: "darkBase",
      }),
      gray10: theme.palette.augmentColor({
        color: {
          main: colorPalette.gray10,
        },
        name: "gray10",
      }),
      gray20: theme.palette.augmentColor({
        color: {
          main: colorPalette.gray20,
        },
        name: "gray20",
      }),
      gray30: theme.palette.augmentColor({
        color: {
          main: colorPalette.gray30,
        },
        name: "gray30",
      }),
      gray40: theme.palette.augmentColor({
        color: {
          main: colorPalette.gray40,
        },
        name: "gray40",
      }),
      gray50: theme.palette.augmentColor({
        color: {
          main: colorPalette.gray50,
        },
        name: "gray50",
      }),
      gray60: theme.palette.augmentColor({
        color: {
          main: colorPalette.gray60,
        },
        name: "gray60",
      }),
      gray80: theme.palette.augmentColor({
        color: {
          main: colorPalette.gray80,
        },
        name: "gray80",
      }),
      gray90: theme.palette.augmentColor({
        color: {
          main: colorPalette.gray90,
        },
        name: "gray90",
      }),
      greenBase: theme.palette.augmentColor({
        color: {
          main: colorPalette.greenBase,
        },
        name: "greenBase",
      }),
      greenLight: theme.palette.augmentColor({
        color: {
          main: colorPalette.greenLight,
        },
        name: "greenLight",
      }),
      pinkLight: theme.palette.augmentColor({
        color: {
          main: colorPalette.pinkLight,
        },
        name: "pinkLight",
      }),
      purpleDark: theme.palette.augmentColor({
        color: {
          main: colorPalette.purpleDark,
        },
        name: "purpleDark",
      }),
      purpleLight: theme.palette.augmentColor({
        color: {
          main: colorPalette.purpleLight,
        },
        name: "purpleLight",
      }),
      redLight: theme.palette.augmentColor({
        color: {
          main: colorPalette.redLight,
        },
        name: "redLight",
      }),
      redMid: theme.palette.augmentColor({
        color: {
          main: colorPalette.redMid,
        },
        name: "redMid",
      }),
      redSmooth: theme.palette.augmentColor({
        color: {
          main: colorPalette.redSmooth,
        },
        name: "redSmooth",
      }),
      tealBase: theme.palette.augmentColor({
        color: {
          main: colorPalette.tealBase,
        },
        name: "tealBase",
      }),
      turkizLight: theme.palette.augmentColor({
        color: {
          main: colorPalette.turkizLight,
        },
        name: "turkizLight",
      }),
      white: theme.palette.augmentColor({
        color: {
          main: colorPalette.white,
        },
        name: "white",
      }),
      yellowBase: theme.palette.augmentColor({
        color: {
          main: colorPalette.yellowBase,
        },
        name: "yellowBase",
      }),
      yellowDark: theme.palette.augmentColor({
        color: {
          main: colorPalette.yellowDark,
        },
        name: "yellowDark",
      }),
      owGray: theme.palette.augmentColor({
        color: {
          main: colorPalette.owGray,
        },
        name: "owGray",
      }),
      fuguOrange: theme.palette.augmentColor({
        color: {
          main: colorPalette.fuguOrange,
        },
        name: "fuguOrange",
      }),
    },
    components: {
      MuiMenuItem: {
        styleOverrides: {
          root: {
            "&.Mui-selected": {
              backgroundColor: `${colorPalette.gray30} !important`,
              "&:hover": {
                backgroundColor: colorPalette.gray10,
              },
            },
            "&.MuiMultiSectionDigitalClockSection-item.Mui-selected": {
              backgroundColor: `${colorPalette.primary} !important`,
            },
          },
        },
      },

      //      MuiPickersDay: {
      //        styleOverrides: {
      //          root: {
      //            "&:not(.Mui-selected)": {
      //              border: "none",
      //            },
      //          },
      //        },
      //      },
      //      MuiPickersLayout: {
      //        styleOverrides: {
      //          root: {
      //            borderRadius: "8px",
      //            display: "flex",
      //            flexDirection: "column",
      //            alignItems: "center",
      //          },
      //        },
      //      },
      MuiTextField: {
        styleOverrides: {
          root: {
            height: "56px",
            background: "transparent",
            margin: 0,
            fontSize: "16px",
            letterSpacing: "0.44px",
          },
        },
      },
      MuiFormHelperText: {
        styleOverrides: {
          root: {
            color: colorPalette.gray80,
            "&$error": {
              color: colorPalette.primary,
            },
          },
        },
      },
      MuiInputLabel: {
        defaultProps: {
          //color: colorPalette.darkBase,
        },
        styleOverrides: {
          root: {
            color: colorPalette.gray80,
            "&.Mui-focused": {
              color: colorPalette.gray80,
            },
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            backgroundColor: colorPalette.gray30,
            color: "red",
            textTransform: "initial",
            fontSize: "16px",
            lineHeight: "20px",
            letterSpacing: "0.44px",
            height: "56px",
            minWidth: "72px",
            padding: "6px 12px",
            fontWeight: "400",
            "@media (min-width:600px)": {
              minWidth: "72px",
              color: colorPalette.gray80,
            },
            "&.Mui-selected": {
              backgroundColor: colorPalette.white,
              borderBottom: `4px solid ${colorPalette.primary}`,
            },
          },
          textColorPrimary: {
            color: colorPalette.gray80,
            "&.Mui-selected": {
              backgroundColor: colorPalette.white,
              color: colorPalette.darkBase,
            },
          },
        },
      },
      MuiOutlinedInput: {
        defaultProps: {
          //color: colorPalette.greenLight,
        },
        styleOverrides: {
          root: {
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: colorPalette.gray40,
              borderWidth: "1px",
            },
            //"&:hover .MuiOutlinedInput-notchedOutline": {
            //borderColor: colorPalette.darkBase,
            //borderWidth: "1px",
            //},
            "&:hover .MuiOutlinedInput": {
              borderColor: colorPalette.darkBase,
              borderWidth: "1px",
            },
            "&.Mui-focused": {
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: colorPalette.gray40,
                borderWidth: "1px",
              },
            },
            "&$focused $notchedOutline": {
              borderColor: colorPalette.greenLight,
              borderWidth: "1px",
            },

            "&$error": {
              borderColor: colorPalette.primary,
            },
          },
        },
      },
      MuiTableSortLabel: {
        styleOverrides: {
          root: {
            "&.Mui-active .MuiTableSortLabel-icon": {
              color: colorPalette.gray80,
            },
          },
        },
      },
      MuiTypography: {
        styleOverrides: {
          root: ({ ownerState }) => ({
            color: colorPalette.darkBase,
            ...(ownerState.color === "info" && {
              color: colorPalette.gray60,
            }),
          }),
        },
      },
      MuiCheckbox: {
        styleOverrides: {
          root: {
            color: colorPalette.gray60,
            "&.Mui-checked": {
              color: colorPalette.gray60,
            },
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          root: {
            "& .MuiSelect-select.MuiInputBase-input.MuiOutlinedInput-input": {
              backgroundColor: "white",
            },
          },
        },
      },
      MuiTablePagination: {
        defaultProps: {
          getItemAriaLabel: (type) => {
            if (type === "first") {
              return "Első oldalra";
            }
            if (type === "last") {
              return "Utolsó oldalra";
            }
            if (type === "next") {
              return "Következő oldalra";
            }
            // if (type === 'previous') {
            return "Előző oldalra";
          },
          labelRowsPerPage: "Sorok száma:",
          labelDisplayedRows: ({ from, to, count }) =>
            `${from}–${to} / ${count !== -1 ? count : `több mint ${to}`}`,
        },
        styleOverrides: {
          root: {
            "& .MuiIconButton-root": {
              padding: "12px",
            },
          },
        },
      },
      MuiPagination: {
        defaultProps: {
          "aria-label": "Lapozás",
          getItemAriaLabel: (type, page, selected) => {
            if (type === "page") {
              return `${page}. oldal${selected ? "" : "ra"}`;
            }
            if (type === "first") {
              return "Első oldalra";
            }
            if (type === "last") {
              return "Utolsó oldalra";
            }
            if (type === "next") {
              return "Következő oldalra";
            }
            // if (type === 'previous') {
            return "Előző oldalra";
          },
        },
      },
      MuiDialogContent: {
        styleOverrides: {
          root: {
            padding: "0px 16px",
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          head: {
            fontSize: "16px",
            letterSpacing: "0.25px",
            color: colorPalette.gray80,
            padding: "10px 8px 10px 8px",
            backgroundColor: colorPalette.gray30,
            "&:last-child": {
              borderTopRightRadius: "4px",
              borderBottomRightRadius: "4px",
            },
            "&:first-of-type": {
              borderTopLeftRadius: "4px",
              borderBottomLeftRadius: "4px",
            },
          },
          stickyHeader: {
            backgroundColor: colorPalette.gray30,
          },
          body: {
            fontSize: "16px",
            letterSpacing: "0.44px",
            padding: "17.5px 8px 17.5px 8px", // With a basic height parameter, there will be an empty row in the table where there is a collapsed section.
            color: colorPalette.gray80,
          },
        },
      },
      MuiButtonGroup: {
        styleOverrides: {
          root: {
            "&.MuiButtonGroup-vertical": {
              ".MuiButtonGroup-grouped:not(.MuiButton-containedPrimary):hover": {
                border: `1px solid ${colorPalette.gray40}`,
              },
            },
          },
          grouped: {
            "&.MuiButtonGroup-grouped": {
              "&.MuiButton-containedPrimary": {
                backgroundColor: colorPalette.primary,
                color: colorPalette.white,
                padding: "9px 20px",
                border: "unset",
              },
              "&:not(.MuiButton-containedPrimary)": {
                backgroundColor: colorPalette.white,
                color: colorPalette.gray60,
                padding: "9px 20px",
                borderColor: colorPalette.gray40,
                "&:hover": {
                  backgroundColor: colorPalette.gray20,
                  border: `1px solid ${colorPalette.gray60}`,
                },
              },
            },
          },
          groupedVertical: {
            "&:not(.MuiButton-containedPrimary)": {
              backgroundColor: colorPalette.white,
              color: colorPalette.gray60,
              padding: "9px 20px",
            },
          },
        },
      },
      MuiRadio: {
        styleOverrides: {
          root: ({ ownerState }) => ({
            ...(ownerState.color === "default" && {
              "&.Mui-checked": {
                color: colorPalette.gray80,
              },
            }),
          }),
        },
      },
      MuiButton: {
        defaultProps: {
          variant: "contained",
          disableElevation: true,
        },
        styleOverrides: {
          root: ({ ownerState }) => ({
            height: "36px",
            padding: "6px 8px",
            ...(ownerState.variant === "outlined" &&
              ownerState.color === "secondary" && {
              color: colorPalette.darkBase,
              backgroundColor: colorPalette.gray30,
              borderColor: colorPalette.gray30,
              "&:hover": {
                color: colorPalette.darkBase,
                backgroundColor: colorPalette.gray40,
                borderColor: colorPalette.gray40,
              },
            }),
            ...(ownerState.variant === "outlined" &&
              ownerState.color !== "secondary" && {
              color: colorPalette.darkBase,
              border: "unset",
              "&:hover": {
                color: colorPalette.darkBase,
                backgroundColor: colorPalette.gray20,
                border: "unset",
              },
            }),
            ...(ownerState.variant === "text" &&
              ownerState.color === "primary" && {
              color: colorPalette.primary,
              "&:hover": {
                color: colorPalette.primary,
              },
            }),
            ...(ownerState.variant === "text" &&
              ownerState.color === "secondary" && {
              color: colorPalette.darkBase,
              "&:hover": {
                color: colorPalette.darkBase,
                backgroundColor: colorPalette.gray30,
              },
            }),
            ...(ownerState.color === "inherit" && {
              color: colorPalette.darkBase,
              border: `1px solid ${colorPalette.gray40}`,
              "&.MuiButton-inherit:hover": {
                color: colorPalette.darkBase,
                backgroundColor: colorPalette.gray20,
                border: `1px solid ${colorPalette.gray40}`,
              },
            }),
            ...(ownerState.variant === "outlined" &&
              !ownerState.color && {
              "&:hover": {
                borderColor: colorPalette.gray40,
              },
            }),
            "&:has(.MuiButton-startIcon)": {
              paddingLeft: "13px",
            },
          }),
        },
      },
      OwDrawerHeader: {
        styleOverrides: {
          root: {
            backgroundColor: "#cc0033",
            color: "#ffffff",
          },
        },
      },
    },
  },
  huHU,
);
